import {
  OntraportField,
  ontraportEmptyField,
} from 'organization/Event/Services/Apps/Ontraport/Config/Fields'
import {OntraportGroup} from 'organization/Event/Services/Apps/Ontraport/Config/Groups'
import {
  ATTENDEE_CHECKED_IN,
  ATTENDEE_CREATED,
  ATTENDEE_SIGNED_WAIVER,
  IMPORT_TAG,
  Tag,
  UPDATE_TAG,
} from 'organization/Event/Services/Apps/Ontraport/Config/Tags'
import {
  useOntraport,
  useServices,
} from 'organization/Event/Services/ServicesProvider'
import Typography from '@material-ui/core/Typography'
import Page from 'organization/Event/Page'
import React, {useState} from 'react'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import {withStyles} from '@material-ui/core'
import ErrorAlert from 'lib/ui/alerts/ErrorAlert'
import {spacing} from 'lib/ui/theme'
import Button from '@material-ui/core/Button'
import {useOrganization} from 'organization/OrganizationProvider'
import {useEvent} from 'Event/EventProvider'
import {api} from 'lib/url'
import {useToggle} from 'lib/toggle'
import InfoAlert from 'lib/ui/alerts/InfoAlert'
import {
  LoginField,
  OntraportIntagration,
} from 'organization/Event/Services/Apps/Ontraport'
import TagSelector from 'organization/Event/Services/Apps/Ontraport/Config/Tags/TagSelector'
import AutocompleteFields from 'organization/Event/Services/Apps/Ontraport/Config/Fields/AutocompleteFields'
import GroupsConfig from 'organization/Event/Services/Apps/Ontraport/Config/Groups/GroupsConfig'
import ImportAttendeesButton from 'organization/Event/Services/Apps/Ontraport/Config/ImportAttendeesButton'

export interface UpdateData {
  tags: Tag[]
  groups: OntraportGroup[]
  login_field: LoginField
}

export default function OntraportConfig() {
  const ontraport = useOntraport()
  const {save, processing, error} = useSave()

  const {flag: showingImportSuccess, toggle: toggleImportSuccess} = useToggle()
  const [importError, setImportError] = useState('')
  const clearImportError = () => setImportError('')

  const [tags, setTags] = useState<Tag[]>(ontraport.tags)
  const [groups, setGroups] = useState<OntraportGroup[]>(ontraport.groups)

  const [loginField, setLoginField] = useState<LoginField>({
    field_id: ontraport.login_field_id ?? '',
    field_name: ontraport.login_field_name ?? '',
    field_type: ontraport.login_field_type ?? '',
    field_key: ontraport.login_field_key ?? '',
  })

  const updateTag = (tag: Tag) => {
    const updated = tags.map((t) => {
      const isTarget = t.id === tag.id

      if (isTarget) {
        return tag
      }

      return t
    })
    setTags(updated)
  }

  const changeLoginField = (field: OntraportField | null) => {
    setLoginField(field ?? ontraportEmptyField)
  }

  const handleSave = () => {
    const data: UpdateData = {
      tags,
      groups,
      login_field: loginField,
    }

    save(data)
  }

  const tagForType = (type: Tag['type']) => {
    const target = tags.find((t) => t.type === type)
    if (!target) {
      throw new Error(`Unknown tag type: ${type}`)
    }

    return target
  }

  return (
    <Page>
      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item md={6}>
            <Typography variant="h4">Ontraport</Typography>
          </Grid>
          <StyledGrid item md={6}>
            <ImportAttendeesButton
              onSuccess={toggleImportSuccess}
              onError={setImportError}
            />
          </StyledGrid>
        </Grid>
      </Box>

      <ErrorAlert onClose={clearImportError}>{importError}</ErrorAlert>
      <InfoAlert showing={showingImportSuccess}>
        Import successfully started. Depending on the number of subscribers.
      </InfoAlert>

      <Box mb={3}>
        <Box mb={2}>
          <Typography variant="h5">
            Attendee Import or Delete via Tag
          </Typography>
        </Box>
        <TagSelector tag={tagForType(IMPORT_TAG)} onChange={updateTag} />
        <em>
          If an attendee with the given email already exists, obv.io will update
          the other attributes.
        </em>
      </Box>

      <Box mb={3}>
        <Box mb={2}>
          <Typography variant="h5">Update attendee via Tag</Typography>
        </Box>
        <TagSelector tag={tagForType(UPDATE_TAG)} onChange={updateTag} />
        <em>Sync attendee to OBVio when tag is applyed.</em>
      </Box>

      <Box mb={2}>
        <Typography variant="h5">Login URL Field</Typography>
        <em>
          Select the Custom Field in Ontraport to save your attendee's Login
          URL.
        </em>
      </Box>

      <AutocompleteFields
        onChange={changeLoginField}
        inputVariant={'outlined'}
        value={loginField}
      />

      <Box mb={3}>
        <Typography variant="h5">Action Tags</Typography>
        <em>
          The following tags can be applied in Ontraport when an attendee takes
          the specific action. (optional)
        </em>
      </Box>

      <TagSelector tag={tagForType(ATTENDEE_CREATED)} onChange={updateTag} />
      <TagSelector tag={tagForType(ATTENDEE_CHECKED_IN)} onChange={updateTag} />
      <TagSelector
        tag={tagForType(ATTENDEE_SIGNED_WAIVER)}
        onChange={updateTag}
      />

      <Box mb={3}>
        <Typography variant="h5">Groups</Typography>
        <em>
          Select additional custom fields you wish to import into your Obvio
          event. (optional)
        </em>
      </Box>
      <Box mb={5}>
        <GroupsConfig onChange={setGroups} groups={groups} />
      </Box>

      <ErrorAlert>{error}</ErrorAlert>
      <SaveButton
        type="submit"
        variant="contained"
        color="primary"
        size="large"
        aria-label="save"
        onClick={handleSave}
        disabled={processing}
      >
        Save
      </SaveButton>
    </Page>
  )
}

function useSave() {
  const {client} = useOrganization()
  const {event} = useEvent()

  const [processing, setProcessing] = useState<boolean>(false)
  const {update: updateIntegration} = useServices()
  const [error, setError] = useState<string | null>(null)

  const save = (data: UpdateData) => {
    setProcessing(true)
    setError('')

    client
      .put<OntraportIntagration>(
        api(`/events/${event.id}/integrations/ontraport`),
        data,
      )
      .then(updateIntegration)
      .catch((e) => setError(e.message))
      .finally(() => {
        setProcessing(false)
      })
  }

  return {
    processing,
    error,
    save,
  }
}

const SaveButton = withStyles({
  root: {
    marginBottom: spacing[5],
  },
})(Button)

const StyledGrid = withStyles({
  root: {
    display: 'flex',
    justifyContent: 'end',
  },
})(Grid)
